import { escapeHtml, renderMarkdown } from './markdown.js';

// Shared rendering for the tier-3 "explain" envelope
// (docs/CONTEXTUAL_EXPLAINERS_DESIGN.md §4, handoff/frontend/tier3-explain-ui.md).
// Findings reuse the exact severity vocabulary/styling as tier-2 callouts
// (`.he-callout*` classes from help-render.js) — callers must adopt
// HELP_ENTRY_STYLES alongside EXPLAIN_STYLES for those classes to resolve.

const SEVERITY_RANK = { critical: 0, warning: 1, info: 2 };
const SEVERITY_LABEL = { critical: 'Critical', warning: 'Warning', info: 'Info' };

export const EXPLAIN_STYLES = `
.ex-section { margin-bottom: 16px; }
.ex-section:last-child { margin-bottom: 0; }
.ex-section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px; color: var(--text-secondary, #888); margin-bottom: 8px; }

.ex-summary-line { font-size: 14px; font-weight: 700; margin-bottom: 6px; }

.ex-finding, .ex-unavailable { margin-bottom: 8px; }
.ex-finding:last-child, .ex-unavailable:last-child { margin-bottom: 0; }
.ex-finding-rule { font-family: monospace; font-size: 10px; opacity: 0.7; margin-left: 4px; }
.ex-finding-title { font-weight: 600; margin: 3px 0; }
.ex-finding-fix { margin-top: 4px; }
.ex-unavailable-subject { font-family: monospace; font-weight: 700; margin-bottom: 3px; }
.ex-tracked-in { font-size: 11px; opacity: 0.75; margin-top: 3px; }

.ex-findings-index { display: flex; flex-direction: column; gap: 2px; }
.ex-finding-index-item {
    all: unset;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px;
    border-radius: 6px;
    font-size: 12px;
    box-sizing: border-box;
    width: 100%;
}
.ex-finding-index-item:hover { background: var(--bg-secondary, #f0f0f0); }
.ex-finding-index-sev { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.ex-finding-index-sev-critical { background: #c0392b; }
.ex-finding-index-sev-warning { background: #d99a1f; }
.ex-finding-index-sev-info { background: #4d90d9; }
.ex-finding-index-rule { font-family: monospace; font-size: 10px; opacity: 0.7; flex-shrink: 0; }
.ex-finding-index-title { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.ex-fact { padding: 6px 0; border-bottom: 1px solid var(--border-color, #eee); }
.ex-fact:last-child { border-bottom: none; }
.ex-fact-row { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; }
.ex-fact-label { color: var(--text-secondary, #888); font-size: 12px; }
.ex-fact-value { font-family: monospace; font-size: 12.5px; font-weight: 600; text-align: right; }
.ex-fact-callout.he-callout { margin-bottom: 6px; }
.ex-fact-callout .ex-fact-label { color: inherit; font-weight: 600; margin-top: 2px; }
.ex-fact-callout .ex-fact-value { font-weight: 700; text-align: left; }

.ex-provenance {
    font-size: 10.5px;
    color: var(--text-secondary, #999);
    font-family: monospace;
    margin-top: 2px;
    opacity: 0.85;
}

.ex-steps { display: flex; flex-direction: column; gap: 10px; }
.ex-step { border: 1px solid var(--border-color, #e0e0e0); border-radius: 8px; padding: 10px 12px; }
.ex-step-focused { border-color: var(--accent-color, #0066cc); box-shadow: 0 0 0 1px var(--accent-color, #0066cc); }
.ex-step-title { font-weight: 700; font-family: monospace; font-size: 12.5px; margin-bottom: 4px; }
.ex-step-facts { margin-top: 6px; }
.ex-step-findings { margin-top: 6px; }

.ex-links { display: flex; flex-wrap: wrap; gap: 6px; }
.ex-link {
    all: unset;
    cursor: pointer;
    font-size: 11px;
    font-family: monospace;
    padding: 3px 8px;
    border-radius: 10px;
    background: var(--bg-secondary, #eef2f7);
    color: var(--accent-color, #0066cc);
    border: 1px solid var(--border-color, #dbe3ec);
}
.ex-link:hover { background: var(--accent-color, #0066cc); color: #fff; }
`;

function sortBySeverity(list) {
    return [...list].sort((a, b) => (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3));
}

function renderFinding(f) {
    const sev = SEVERITY_RANK[f.severity] !== undefined ? f.severity : 'info';
    return `<div class="ex-finding he-callout he-callout-${sev}">
        <span class="he-callout-label">${escapeHtml(SEVERITY_LABEL[sev] || sev)}</span>
        <span class="ex-finding-rule">${escapeHtml(f.rule || '')}</span>
        <div class="ex-finding-title">${escapeHtml(f.title || '')}</div>
        ${f.detail_md ? renderMarkdown(f.detail_md) : ''}
        ${f.fix_md ? `<div class="ex-finding-fix"><strong>Fix:</strong> ${renderMarkdown(f.fix_md)}</div>` : ''}
    </div>`;
}

// certainty drives rendering, not just styling (spec table, §4.2):
// exact = plain, estimated = "~"-prefixed, unknown/unimplemented = a visible
// callout that is never silently dropped even though it "looks empty".
function renderFact(fact) {
    const { label, display, certainty, derived_from, inputs } = fact;
    const provenance = derived_from
        ? `<div class="ex-provenance">↳ ${escapeHtml(derived_from)}${inputs?.length ? ` (inputs: ${inputs.map(i => escapeHtml(i)).join(', ')})` : ''}</div>`
        : '';

    if (certainty === 'unknown' || certainty === 'unimplemented') {
        const sev = certainty === 'unimplemented' ? 'critical' : 'warning';
        const note = certainty === 'unimplemented' ? 'Not implemented' : 'Unknown';
        return `<div class="ex-fact ex-fact-callout he-callout he-callout-${sev}">
            <span class="he-callout-label">${note}</span>
            <div class="ex-fact-label">${escapeHtml(label ?? '')}</div>
            ${display ? `<div class="ex-fact-value">${escapeHtml(String(display))}</div>` : ''}
            ${provenance}
        </div>`;
    }

    const text = String(display ?? '');
    const displayText = certainty === 'estimated' && !text.startsWith('~') ? `~${text}` : text;
    return `<div class="ex-fact">
        <div class="ex-fact-row"><span class="ex-fact-label">${escapeHtml(label ?? '')}</span><span class="ex-fact-value">${escapeHtml(displayText)}</span></div>
        ${provenance}
    </div>`;
}

function renderSummary(summary) {
    if (!summary) return '';
    return `<div class="ex-section ex-summary">
        ${summary.one_line ? `<div class="ex-summary-line">${escapeHtml(summary.one_line)}</div>` : ''}
        ${summary.detail_md ? renderMarkdown(summary.detail_md) : ''}
    </div>`;
}

// Rendered prominently, not skipped — an unavailable entry is an honest
// answer ("this can't be determined / doesn't exist yet"), not empty data.
// Severity is chosen from the entry's `reason` so a transient "unavailable"
// (mid-edit unresolved step) doesn't render as loud as an "unimplemented" gap.
const UNAVAILABLE_SEVERITY = { unimplemented: 'critical', unavailable: 'warning' };
function renderUnavailable(list) {
    if (!list?.length) return '';
    const items = list.map(u => {
        const sev = UNAVAILABLE_SEVERITY[u.reason] || 'warning';
        return `<div class="ex-unavailable he-callout he-callout-${sev}">
            <span class="he-callout-label">Unavailable</span>
            ${u.subject ? `<div class="ex-unavailable-subject">${escapeHtml(u.subject)}</div>` : ''}
            ${u.detail_md ? renderMarkdown(u.detail_md) : ''}
            ${u.tracked_in ? `<div class="ex-tracked-in">Tracked in: ${escapeHtml(u.tracked_in)}</div>` : ''}
        </div>`;
    }).join('');
    return `<div class="ex-section"><div class="ex-section-title">Unavailable</div>${items}</div>`;
}

function renderFindingsList(findings) {
    if (!findings?.length) return '';
    return `<div class="ex-section"><div class="ex-section-title">Findings</div>${sortBySeverity(findings).map(renderFinding).join('')}</div>`;
}

// Used instead of renderFindingsList() when a step walkthrough is present —
// the same finding already renders in full at its step (via renderSteps'
// step_id join), so the top-level array becomes a scroll-to index rather
// than a second full render of the same problem (tier3-pipeline-walkthrough
// handoff: "do not render it as two separate problems").
function renderFindingsIndex(findings) {
    if (!findings?.length) return '';
    const items = sortBySeverity(findings).map(f => `<button type="button" class="ex-finding-index-item" data-explain-scroll-step="${escapeHtml(f.step_id ?? '')}">
        <span class="ex-finding-index-sev ex-finding-index-sev-${f.severity}"></span>
        <span class="ex-finding-index-rule">${escapeHtml(f.rule || '')}</span>
        <span class="ex-finding-index-title">${escapeHtml(f.title || '')}</span>
    </button>`).join('');
    return `<div class="ex-section"><div class="ex-section-title">Findings (${findings.length})</div><div class="ex-findings-index">${items}</div></div>`;
}

function renderFactsList(facts) {
    if (!facts?.length) return '';
    return `<div class="ex-section"><div class="ex-section-title">Facts</div>${facts.map(renderFact).join('')}</div>`;
}

// Findings are joined to steps by `step_id` here, from the envelope's
// top-level `findings[]` — never by `step_index`, which shifts when a step
// is inserted (tier3-explain-ui.md task 3). `focusStepIndex` is a fallback
// for callers that only know a step's position at click time (the step_id
// is a server-computed hash, unknown until the response arrives).
function renderSteps(steps, findings, { focusStepId, focusStepIndex } = {}) {
    if (!steps?.length) return '';
    const items = steps.map(step => {
        const stepFindings = (findings || []).filter(f => f.step_id === step.step_id);
        const focused = (focusStepId != null && step.step_id === focusStepId)
            || (focusStepIndex != null && step.step_index === focusStepIndex);
        return `<div class="ex-step${focused ? ' ex-step-focused' : ''}" data-step-id="${escapeHtml(step.step_id ?? '')}">
            <div class="ex-step-title">${escapeHtml(step.title || `Step ${step.step_index}`)}</div>
            ${step.detail_md ? renderMarkdown(step.detail_md) : ''}
            ${step.facts?.length ? `<div class="ex-step-facts">${step.facts.map(renderFact).join('')}</div>` : ''}
            ${stepFindings.length ? `<div class="ex-step-findings">${stepFindings.map(renderFinding).join('')}</div>` : ''}
        </div>`;
    }).join('');
    return `<div class="ex-section"><div class="ex-section-title">Step-by-step</div><div class="ex-steps">${items}</div></div>`;
}

// concepts[]/help_keys[] link into the tier-1/tier-2 surfaces already built
// — the caller wires `[data-explain-nav]` clicks to those, not a new lookup.
function renderExplainLinks(concepts, helpKeys) {
    const items = [
        ...(concepts || []).map(k => `<button type="button" class="ex-link" data-explain-nav="concept:${escapeHtml(k)}">${escapeHtml(k)}</button>`),
        ...(helpKeys || []).map(k => `<button type="button" class="ex-link" data-explain-nav="help:${escapeHtml(k)}">${escapeHtml(k)}</button>`),
    ];
    if (!items.length) return '';
    return `<div class="ex-section"><div class="ex-section-title">Learn more</div><div class="ex-links">${items.join('')}</div></div>`;
}

// Suggested section order from tier3-explain-ui.md task 2: summary,
// unavailable, findings, facts, step walkthrough, then concepts/help links.
export function renderEnvelope(envelope, { focusStepId, focusStepIndex } = {}) {
    if (!envelope) return '';
    const hasSteps = !!envelope.steps?.length;
    const sections = [
        renderSummary(envelope.summary),
        renderUnavailable(envelope.unavailable),
        hasSteps ? renderFindingsIndex(envelope.findings) : renderFindingsList(envelope.findings),
        renderFactsList(envelope.facts),
        renderSteps(envelope.steps, envelope.findings, { focusStepId, focusStepIndex }),
        renderExplainLinks(envelope.concepts, envelope.help_keys),
    ];
    return sections.filter(Boolean).join('') || '<div class="ex-section">Nothing to explain yet.</div>';
}
